import React from 'react';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import {
  Message, MessageType, TripRequest, TripRequestType,
} from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../../store';
import useZicassoChat from '../../../../hooks/use-zicasso-chat';
import ConversationView from './conversation-view';
import debugPromise from '../../../../utils/debug-promise';

type Props = {
  conversationId?: string | undefined,
  conversationTitle?: string,
};

const ConversationContainer = ({
  conversationId = undefined,
  conversationTitle = undefined,
}: Props) => {
  const { loggedInTraveler } = useAuthStore();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { tripRequestId } = useParams<{ tripRequestId: string }>();
  const {
    isError,
    isLoading,
    isValidating,
    conversation,
    messages,
    participants,
    sendMessage,
    lastSeenMessageId,
    loadMoreMessages,
    hasMoreMessages,
  } = useZicassoChat({
    conversationId,
    viewerId: loggedInTraveler?.id,
    tripRequestId,
  });
  const viewer = {
    id: loggedInTraveler?.id,
    type: PersonType.Traveler,
  };
  const { data: tripRequest } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);

  const primaryTraveler = tripRequest?.travelers[0];
  const isCoTraveler = viewer.id !== primaryTraveler?.id;
  const primaryTravelerMessageState = conversation?.messageState.find((messageState) => messageState.personId === primaryTraveler?.id);
  const ZERO_DATE_STRING = '0001-01-01T00:00:00Z'; // This is the date that is returned if no message has been received yet
  const lastReceivedAt = primaryTravelerMessageState?.lastMessageReceivedAt;
  const didZicassoMessagePrimaryTraveler = !!lastReceivedAt && lastReceivedAt !== ZERO_DATE_STRING;

  // Read Only for co-travelers if Zicasso hasn't messaged the primary traveler yet
  const readOnly = !!primaryTraveler && isCoTraveler && !didZicassoMessagePrimaryTraveler;

  const handleOnSend = (messageText: string) => {
    debugPromise('tripRequestConversationContainer:sendMessage', sendMessage({
      conversationId,
      text: messageText,
      messageType: MessageType.Text,
    }));
  };

  const handleDocumentClick = async (message: Message) => {
    if (!message.entityId) {
      return;
    }

    try {
      const doc = await apiClient.documentsClient.getDocument(message.entityId);
      if (doc.sourceURI) {
        window.open(doc.sourceURI, '_blank');
        return;
      }

      const downloadURL = await apiClient.documentsClient.getDocumentDownloadUrl(message.entityId);
      window.open(downloadURL.url, '_blank');
    } catch (error: any) {
      const snackbarMessage = error?.code === 404 ? 'Document not found' : 'Error downloading document';
      enqueueSnackbar(snackbarMessage, { variant: 'default' });
    }
  };

  const handleTripMatchReviewRequestClick = async (message: Message) => {
    if (message.entityId) {
      navigate(`/trip-matches/${message.entityId}/review`);
    }
  };

  const handleViewClick = (message: Message) => {
    switch (message.entityType) {
      case 'Document':
        debugPromise('tripRequestConversationContainer:viewClick.document', handleDocumentClick(message));
        break;
      case 'TripMatchReviewRequest':
        debugPromise('tripRequestConversationContainer:viewClick.tripMatchReviewRequest', handleTripMatchReviewRequestClick(message));
        break;
      case 'SalesRecording':
        break;
      default:
        break;
    }
  };

  return (
    <ConversationView
      conversation={conversation}
      conversationTitle={conversationTitle}
      handleViewClick={handleViewClick}
      isError={isError}
      isLoading={isLoading}
      isValidating={isValidating}
      messages={messages}
      lastMessageSeenId={lastSeenMessageId}
      participants={participants}
      sendMessage={handleOnSend}
      loadMoreMessages={loadMoreMessages}
      hasMoreMessages={hasMoreMessages}
      viewer={viewer}
      tripRequest={tripRequest}
      isLegacyTR={tripRequest?.tripRequestType === TripRequestType.Legacy}
      readOnly={readOnly}
    />
  );
};

export default ConversationContainer;
